
import { AssessmentSection, AssessmentResponse, AssessmentResult, SkillLevelInfo, PlayingStyleData } from '@/types/skillAssessment';

export const SKILL_LEVELS: SkillLevelInfo[] = [
  {
    level: 'beginner',
    title: 'Beginner (Bronze)',
    eloRange: '0.0 - 2.5',
    description: 'Fundamental skills, basic understanding, learning phase',
    characteristics: [
      'New to Padel or very limited experience',
      'Learning basic rules, scoring, and court positioning',
      'Developing fundamental strokes and ball control',
      'Focus on consistency and understanding the game',
      'Typically 0-12 months of playing experience'
    ]
  },
  {
    level: 'amateur',
    title: 'Amateur (Silver)',
    eloRange: '2.6 - 5.0',
    description: 'Developing expertise, growing proficiency, intermediate competence',
    characteristics: [
      'Regular recreational players with solid fundamentals',
      'Good understanding of rules and basic strategy',
      'Consistent groundstrokes and developing net play',
      'Can maintain rallies and play organized points',
      'Typically 1-3 years of regular playing experience'
    ]
  },
  {
    level: 'advanced',
    title: 'Advanced (Gold)',
    eloRange: '5.1 - 7.5',
    description: 'High-level skills, refined techniques, consistent performance',
    characteristics: [
      'Competitive players with strong all-around game',
      'Excellent technique and tactical understanding',
      'Comfortable with advanced shots and strategies',
      'Competes in tournaments and club competitions',
      'Typically 3+ years with dedicated improvement focus'
    ]
  },
  {
    level: 'expert',
    title: 'Expert (Platinum)',
    eloRange: '7.6 - 10.0',
    description: 'Mastery, expert execution, top-tier excellence',
    characteristics: [
      'Tournament-level players with mastery of all aspects',
      'Professional or semi-professional level technique',
      'Advanced tactical knowledge and court sense',
      'Competes at regional, national, or international level',
      'Extensive competitive experience and achievements'
    ]
  }
];

export const ASSESSMENT_SECTIONS: AssessmentSection[] = [
  {
    id: 'experience',
    title: 'Playing Experience',
    description: 'Your background and playing frequency',
    weight: 0.25,
    questions: [
      {
        id: 'exp_duration',
        text: 'How long have you been playing Padel?',
        section: 'experience',
        options: [
          { value: 0, label: 'Never played before' },
          { value: 1, label: 'Less than 6 months' },
          { value: 2, label: '6 months to 1 year' },
          { value: 3, label: '1-2 years' },
          { value: 4, label: '2-3 years' },
          { value: 5, label: '3-5 years' },
          { value: 6, label: 'More than 5 years' }
        ]
      },
      {
        id: 'exp_frequency',
        text: 'How often do you currently play Padel?',
        section: 'experience',
        options: [
          { value: 0, label: 'Never/Just starting' },
          { value: 1, label: 'Once a month or less' },
          { value: 2, label: '2-3 times per month' },
          { value: 3, label: 'Once a week' },
          { value: 4, label: '2-3 times per week' },
          { value: 5, label: '4-5 times per week' },
          { value: 6, label: 'Daily or almost daily' }
        ]
      },
      {
        id: 'exp_background',
        text: 'What is your primary playing background?',
        section: 'experience',
        options: [
          { value: 0, label: 'Complete beginner to racquet sports' },
          { value: 1, label: 'Played other racquet sports casually' },
          { value: 2, label: 'Experienced in tennis recreationally' },
          { value: 2, label: 'Experienced in squash or racquetball' },
          { value: 3, label: 'Competitive tennis background' },
          { value: 4, label: 'Professional/semi-professional racquet sports' }
        ]
      }
    ]
  },
  {
    id: 'technical',
    title: 'Technical Skills',
    description: 'Your stroke technique and court skills',
    weight: 0.30,
    questions: [
      {
        id: 'tech_groundstrokes',
        text: 'How would you rate your forehand and backhand groundstrokes?',
        section: 'technical',
        options: [
          { value: 0, label: 'Still learning basic strokes' },
          { value: 1, label: 'Can hit the ball but inconsistent' },
          { value: 2, label: 'Consistent contact, limited power/placement' },
          { value: 3, label: 'Good consistency with moderate power' },
          { value: 4, label: 'Strong, controlled shots with good placement' },
          { value: 5, label: 'Excellent technique with power and precision' },
          { value: 6, label: 'Advanced shots with spin and tactical variation' }
        ]
      },
      {
        id: 'tech_volleys',
        text: 'How comfortable are you with volleys at the net?',
        section: 'technical',
        options: [
          { value: 0, label: 'Avoid coming to the net' },
          { value: 1, label: 'Basic volleys but prefer baseline' },
          { value: 2, label: 'Comfortable with simple volleys' },
          { value: 3, label: 'Good volley technique, can handle most shots' },
          { value: 4, label: 'Strong net play, quick reflexes' },
          { value: 5, label: 'Excellent net skills, can dominate at net' },
          { value: 6, label: 'Advanced net play with angles and drop shots' }
        ]
      },
      {
        id: 'tech_serve',
        text: 'How would you rate your serve?',
        section: 'technical',
        options: [
          { value: 0, label: 'Learning basic serve motion' },
          { value: 1, label: 'Can get serve in play consistently' },
          { value: 2, label: 'Basic serve with some power' },
          { value: 3, label: 'Good serve with placement control' },
          { value: 4, label: 'Strong serve with variety (speed/placement)' },
          { value: 5, label: 'Powerful serve that wins points' },
          { value: 6, label: 'Advanced serve with spin and tactical variety' }
        ]
      }
    ]
  },
  {
    id: 'knowledge',
    title: 'Game Knowledge & Strategy',
    description: 'Your understanding of rules and tactics',
    weight: 0.25,
    questions: [
      {
        id: 'know_rules',
        text: 'How well do you understand Padel scoring and rules?',
        section: 'knowledge',
        options: [
          { value: 0, label: 'Still learning the basics' },
          { value: 1, label: 'Know basic rules but sometimes confused' },
          { value: 2, label: 'Understand most rules and scoring' },
          { value: 3, label: 'Know all standard rules well' },
          { value: 4, label: 'Know rules including advanced situations' },
          { value: 5, label: 'Complete understanding including referee-level knowledge' }
        ]
      },
      {
        id: 'know_tactics',
        text: 'How would you rate your tactical understanding?',
        section: 'knowledge',
        options: [
          { value: 0, label: 'Focus mainly on hitting the ball' },
          { value: 1, label: 'Understand basic court positioning' },
          { value: 2, label: 'Know when to attack and defend' },
          { value: 3, label: 'Good understanding of shot selection' },
          { value: 4, label: 'Strong tactical awareness, can read the game' },
          { value: 5, label: 'Advanced strategy, can outthink opponents' },
          { value: 6, label: 'Expert level game management and tactics' }
        ]
      },
      {
        id: 'know_doubles',
        text: 'How well do you communicate and coordinate with doubles partners?',
        section: 'knowledge',
        options: [
          { value: 0, label: 'Still learning doubles positioning' },
          { value: 1, label: 'Basic communication and movement' },
          { value: 2, label: 'Good coordination with regular partners' },
          { value: 3, label: 'Adapt well to different partners' },
          { value: 4, label: 'Strong leadership and communication skills' },
          { value: 5, label: 'Excellent partnership with any player' },
          { value: 6, label: 'Master level doubles coordination' }
        ]
      }
    ]
  },
  {
    id: 'competition',
    title: 'Competition & Performance',
    description: 'Your competitive experience and performance',
    weight: 0.20,
    questions: [
      {
        id: 'comp_level',
        text: 'What is your highest level of competitive play?',
        section: 'competition',
        options: [
          { value: 0, label: 'No competitive experience' },
          { value: 1, label: 'Casual games with friends' },
          { value: 2, label: 'Club social tournaments' },
          { value: 3, label: 'Local club championships' },
          { value: 4, label: 'Regional tournaments' },
          { value: 5, label: 'National level competitions' },
          { value: 6, label: 'International or professional level' }
        ]
      },
      {
        id: 'comp_performance',
        text: 'How do you typically perform in competitive situations?',
        section: 'competition',
        options: [
          { value: 0, label: 'Get nervous and play below my usual level' },
          { value: 1, label: 'Sometimes struggle under pressure' },
          { value: 2, label: 'Generally maintain my usual level' },
          { value: 3, label: 'Often play better in competitive matches' },
          { value: 4, label: 'Thrive under pressure, raise my game' },
          { value: 5, label: 'Consistently perform at peak level in competition' }
        ]
      }
    ]
  },
  {
    id: 'playing_style',
    title: 'Playing Style',
    description: 'Your playing preferences and style (does not affect your rating)',
    weight: 0,
    questions: [
      {
        id: 'style_preferred_side',
        text: 'Which side of the court do you prefer to play?',
        section: 'playing_style',
        options: [
          { value: 1, label: 'Left (Backhand side)', description: 'You prefer the backhand side of the court' },
          { value: 2, label: 'Right (Drive side)', description: 'You prefer the forehand/drive side' },
          { value: 3, label: 'No preference', description: 'You are comfortable on either side' }
        ]
      },
      {
        id: 'style_play_type',
        text: 'How would you describe your play type?',
        section: 'playing_style',
        options: [
          { value: 1, label: 'Offensive', description: 'Aggressive at the net, looking to finish points' },
          { value: 2, label: 'Defensive', description: 'Patient baseline play, waiting for opportunities' },
          { value: 3, label: 'All-round', description: 'Balanced mix of attack and defense' }
        ]
      },
      {
        id: 'style_preferred_position',
        text: 'Where do you prefer to position yourself on court?',
        section: 'playing_style',
        options: [
          { value: 1, label: 'Net', description: 'You like to dominate at the net with volleys' },
          { value: 2, label: 'Back', description: 'You prefer staying at the back of the court' },
          { value: 3, label: 'Both', description: 'You move fluidly between net and back' }
        ]
      },
      {
        id: 'style_power_control',
        text: 'Do you lean more towards power or control?',
        section: 'playing_style',
        options: [
          { value: 1, label: 'Power', description: 'Strong shots, smashes, and aggressive play' },
          { value: 2, label: 'Control', description: 'Precision placement and touch shots' },
          { value: 3, label: 'Balanced', description: 'A mix of power and finesse' }
        ]
      },
      {
        id: 'style_smash_proficiency',
        text: 'How would you rate your smash and bandeja game?',
        section: 'playing_style',
        options: [
          { value: 1, label: 'Beginner', description: 'Still learning overhead shots' },
          { value: 2, label: 'Intermediate', description: 'Can execute basic smashes and bandejas' },
          { value: 3, label: 'Advanced', description: 'Confident with varied overhead techniques' },
          { value: 4, label: 'Expert', description: 'Dominant overhead game with power and placement' }
        ]
      },
      {
        id: 'style_rally_tempo',
        text: 'What rally tempo do you prefer?',
        section: 'playing_style',
        options: [
          { value: 1, label: 'Slow', description: 'Lob-heavy, patient rallies' },
          { value: 2, label: 'Fast', description: 'Quick exchanges, flat shots, fast pace' },
          { value: 3, label: 'Variable', description: 'You mix tempos to keep opponents guessing' }
        ]
      }
    ]
  }
];

export function calculateAssessmentResult(responses: AssessmentResponse[]): AssessmentResult {
  let totalScore = 0;
  const sectionScores = {
    experience: 0,
    technical: 0,
    knowledge: 0,
    competition: 0
  };
  
  // Only score the 4 main sections (not playing_style)
  const scoredSections = ASSESSMENT_SECTIONS.filter(s => s.id !== 'playing_style');
  
  scoredSections.forEach(section => {
    let sectionTotal = 0;
    let sectionMax = 0;
    
    section.questions.forEach(question => {
      const response = responses.find(r => r.questionId === question.id);
      const score = response ? response.value : 0;
      sectionTotal += score;
      sectionMax += Math.max(...question.options.map(o => o.value));
    });
    
    sectionScores[section.id as keyof typeof sectionScores] = sectionTotal;
    totalScore += sectionTotal;
  });
  
  // Calculate weighted score (only from scored sections)
  let weightedScore = 0;
  scoredSections.forEach(section => {
    const sectionMax = section.questions.reduce((max, question) => 
      max + Math.max(...question.options.map(o => o.value)), 0);
    const sectionPercentage = sectionScores[section.id as keyof typeof sectionScores] / sectionMax;
    weightedScore += sectionPercentage * section.weight * 62;
  });
  
  // Extract playing style preferences
  const playingStyle = extractPlayingStyle(responses);
  
  // Determine skill level and ELO rating
  const { skillLevel, eloRating, needsValidation } = determineSkillLevel(Math.round(weightedScore));
  
  return {
    totalScore,
    weightedScore: Math.round(weightedScore),
    skillLevel,
    eloRating,
    sectionScores,
    needsValidation,
    playingStyle
  };
}

const STYLE_VALUE_MAP: Record<string, Record<number, string>> = {
  style_preferred_side: { 1: 'left', 2: 'right', 3: 'no_preference' },
  style_play_type: { 1: 'offensive', 2: 'defensive', 3: 'all_round' },
  style_preferred_position: { 1: 'net', 2: 'back', 3: 'both' },
  style_power_control: { 1: 'power', 2: 'control', 3: 'balanced' },
  style_smash_proficiency: { 1: 'beginner', 2: 'intermediate', 3: 'advanced', 4: 'expert' },
  style_rally_tempo: { 1: 'slow', 2: 'fast', 3: 'variable' },
};

function extractPlayingStyle(responses: AssessmentResponse[]): PlayingStyleData {
  const getValue = (questionId: string): string | null => {
    const response = responses.find(r => r.questionId === questionId);
    if (!response) return null;
    return STYLE_VALUE_MAP[questionId]?.[response.value] || null;
  };

  return {
    preferred_side: getValue('style_preferred_side'),
    play_type: getValue('style_play_type'),
    preferred_position: getValue('style_preferred_position'),
    power_control: getValue('style_power_control'),
    smash_proficiency: getValue('style_smash_proficiency'),
    rally_tempo: getValue('style_rally_tempo'),
  };
}

function determineSkillLevel(score: number): { 
  skillLevel: 'beginner' | 'amateur' | 'advanced' | 'expert';
  eloRating: number;
  needsValidation: boolean;
} {
  if (score <= 15) {
    // Beginner
    let eloRating = 1.0;
    if (score >= 11) eloRating = 2.0;
    else if (score >= 6) eloRating = 1.5;
    
    return { skillLevel: 'beginner', eloRating, needsValidation: false };
  } else if (score <= 35) {
    // Amateur
    let eloRating = 2.6;
    if (score >= 31) eloRating = 4.4;
    else if (score >= 26) eloRating = 3.8;
    else if (score >= 21) eloRating = 3.2;
    
    return { skillLevel: 'amateur', eloRating, needsValidation: false };
  } else if (score <= 50) {
    // Advanced
    let eloRating = 5.1;
    if (score >= 46) eloRating = 6.5;
    else if (score >= 41) eloRating = 5.8;
    
    return { skillLevel: 'advanced', eloRating, needsValidation: true };
  } else {
    // Expert
    let eloRating = 7.6;
    if (score >= 61) eloRating = 9.5;
    else if (score >= 56) eloRating = 8.5;
    
    return { skillLevel: 'expert', eloRating, needsValidation: true };
  }
}

export function getSkillLevelInfo(level: 'beginner' | 'amateur' | 'advanced' | 'expert'): SkillLevelInfo {
  return SKILL_LEVELS.find(sl => sl.level === level) || SKILL_LEVELS[0];
}

export function getRatingCategory(rating: number): string {
  if (rating >= 7.6) return 'Expert (Platinum)';
  if (rating >= 5.1) return 'Advanced (Gold)';
  if (rating >= 2.6) return 'Amateur (Silver)';
  return 'Beginner (Bronze)';
}

export function getRatingDescription(rating: number): string {
  if (rating >= 7.6) return 'Mastery, expert execution, and top-tier excellence';
  if (rating >= 5.1) return 'High-level skills, refined techniques, and consistent performance';
  if (rating >= 2.6) return 'Developing expertise, growing proficiency, and intermediate competence';
  return 'Fundamental skills, basic understanding, and learning phase';
}
